import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  MiDetailFields,
  MiList,
  MiModal,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
  RECORD_TYPES,
} from "@miplatform/constants";
import { IIssueTemplateResponse, IVerticalTabProps } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  mdiFileDocumentOutline,
  mdiPencilOutline,
  mdiTrashCanOutline,
} from "@mdi/js";
import { AddButton, VerticalTabsLayout } from "@mibuildweb/components";
import { useIssueTemplates, usePermissions } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { schema } from "./schema/schema";
import { ConfigPageLayout } from "../ConfigPageLayout";
import { ConfigCard } from "../components";
import { IIssueTemplateData } from "./ConfigIssueTemplateList.types";

const RECORD_TYPE_MAP = {
  recordables: RECORD_TYPES.RECORDABLE,
  warrantyItems: RECORD_TYPES.SERVICE_ITEM,
};

const LABEL_MAP = {
  recordables: "Recordable",
  warrantyItems: "Warranty Item",
};

const PERMISSION_MAP = {
  recordables: PERMISSIONS.RECORDABLE_TEMPLATE,
  warrantyItems: PERMISSIONS.SERVICE_ITEM_TEMPLATE,
};

export function ConfigIssueTemplatesList({
  view,
}: ConfigIssueTemplatesListProps) {
  const [selectedIssueTemplate, setSelectedIssueTemplate] =
    useState<IIssueTemplateResponse | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [refreshPagination, setRefreshPagination] = useState(false);

  const recordType = RECORD_TYPE_MAP[view];
  const typeLabel = LABEL_MAP[view];
  const permissionEntity = PERMISSION_MAP[view];

  const defaultValues: IIssueTemplateData = {
    name: "",
    recordType,
    canBeSelfResolved: false,
    canBeNotStarted: false,
  };

  const tabs = [
    {
      label: "All",
      value: "All",
      filter: { recordType },
    },
  ];
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const {
    formState: { isValid, isDirty },
    control,
    getValues,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(schema),
  });

  const {
    isLoading,
    issueTemplates,
    getIssueTemplates,
    setIssueTemplates,
    getRecentIssueTemplates,
    setRecentIssueTemplates,
    recentIssueTemplates,
    handleDeleteIssueTemplate,
    refresh,
    setSearch,
    search,
    setFilter,
    filter,
    debouncedSearch,
    handleCreateIssueTemplate,
    handleUpdateIssueTemplate,
  } = useIssueTemplates({
    initialFilter: { recordType },
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleCloseAddModal = () => {
    setSelectedIssueTemplate(null);
    setOpenAddModal(false);
    reset();
  };

  const handleOpenDeleteModal = (item: IIssueTemplateResponse) => {
    setSelectedIssueTemplate(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    if (selectedIssueTemplate) {
      handleDeleteIssueTemplate(selectedIssueTemplate);
      setSelectedIssueTemplate(null);
      setOpenDeleteModal(false);
    }
  };

  const handleTabChange = (
    tab?: IVerticalTabProps<IIssueTemplateResponse[]> | null
  ) => {
    setFilter(tab?.filter || {});
    setSearch("");
  };

  const handleAddIssueTemplate = () => {
    setOpenAddModal(true);
    setSelectedIssueTemplate(null);
    reset(defaultValues);
  };

  const handleSubmitIssueTemplate = () => {
    const data = getValues();
    if (selectedIssueTemplate?.id) {
      handleUpdateIssueTemplate(data);
    } else {
      handleCreateIssueTemplate(data);
    }
    handleCloseAddModal();
  };

  const issueTemplateFields = [
    {
      label: "Name",
      name: "name",
      required: true,
      formControl: control,
      width: "100%",
    },
  ];

  const refreshData = useMemo(() => ({ refresh, filter }), [refresh, filter]);

  useEffect(() => {
    setFilter({ recordType });
    setSearch("");
    setRefreshPagination((prev) => !prev);
  }, [view]);

  return (
    <ConfigPageLayout
      title={`${typeLabel}s`}
      search={search}
      onHandleSearch={handleSearchChange}
      searchPlaceholder="Search"
      hideOnBack
      loading={isLoading}
      titleActionComponent={
        hasPermission(permissionEntity?.CREATE) && (
          <AddButton
            label={`Add ${typeLabel}`}
            onClick={handleAddIssueTemplate}
          />
        )
      }
    >
      <VerticalTabsLayout
        onTabChange={handleTabChange}
        hideStatusIcons
        tabs={tabs}
        hideNavigationButtons
      >
        <MiList
          viewType="card"
          data={issueTemplates ?? []}
          dataProvider={getIssueTemplates}
          setData={setIssueTemplates}
          loading={isLoading}
          CardComponent={ConfigIssueTemplatesCard}
          cardProps={{
            permissionEntity,
            handleOpenDeleteModal,
            setOpenAddModal,
            setSelectedIssueTemplate,
            reset,
          }}
          paginationMode={PAGINATION.SERVER}
          search={debouncedSearch}
          recentData={recentIssueTemplates}
          setRecentData={setRecentIssueTemplates}
          recentDataProvider={getRecentIssueTemplates}
          entityName={typeLabel}
          refresh={refreshData}
          refreshPagination={refreshPagination}
          emptyListProps={{
            text: `No ${typeLabel}s found`,
          }}
        />
      </VerticalTabsLayout>
      <MiModal
        open={openAddModal}
        titleIcon={
          selectedIssueTemplate?.id ? mdiPencilOutline : mdiFileDocumentOutline
        }
        loading={isLoading}
        title={
          selectedIssueTemplate?.id
            ? `Edit ${typeLabel}`
            : `Add New ${typeLabel}`
        }
        description={
          selectedIssueTemplate?.id
            ? `Edit ${typeLabel} details below.`
            : `Add ${typeLabel} to your org.`
        }
        closeModal={handleCloseAddModal}
        actions={[
          {
            name: BUTTON_LABELS.CANCEL,
            variant: BUTTON_VARIANTS.TEXT,
            onClick: handleCloseAddModal,
          },
          {
            name: `${
              selectedIssueTemplate?.id ? "Update" : "Save"
            } ${typeLabel}`,
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: handleSubmitIssueTemplate,
            disabled: !isDirty || !isValid || isLoading,
          },
        ]}
      >
        <MiDetailFields detailFields={issueTemplateFields} />
      </MiModal>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={() => setOpenDeleteModal(false)}
        handlePositive={handleConfirmDeleteModal}
        description={`You are about to delete ${selectedIssueTemplate?.name}. Are you sure you would like to continue?`}
        buttonPositiveColor="error"
        titleIcon={{
          path: mdiTrashCanOutline,
          color: theme.palette.red.main,
        }}
      />
    </ConfigPageLayout>
  );
}

const ConfigIssueTemplatesCard = ({
  item,
  permissionEntity,
  handleOpenDeleteModal,
  setOpenAddModal,
  setSelectedIssueTemplate,
  reset,
}: {
  item: IIssueTemplateResponse;
  permissionEntity:
    | typeof PERMISSIONS.RECORDABLE_TEMPLATE
    | typeof PERMISSIONS.SERVICE_ITEM_TEMPLATE;
  handleOpenDeleteModal: (item: IIssueTemplateResponse) => void;
  setOpenAddModal: (open: boolean) => void;
  setSelectedIssueTemplate: (item: IIssueTemplateResponse) => void;
  reset: (data?: IIssueTemplateResponse) => void;
}) => {
  const { name, modifiedOn, createdOn } = item;
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const handleOpenEditModal = () => {
    setOpenAddModal(true);
    setSelectedIssueTemplate(item);
    reset(item);
  };

  const actions = [
    {
      label: BUTTON_LABELS.EDIT,
      action: handleOpenEditModal,
      icon: mdiPencilOutline,
      permission: permissionEntity?.UPDATE,
    },
    {
      label: BUTTON_LABELS.DELETE,
      action: () => handleOpenDeleteModal(item),
      icon: mdiTrashCanOutline,
      color: theme.palette.red.main,
      permission: permissionEntity?.DELETE,
    },
  ].filter((action) => hasPermission(action.permission));

  return (
    <ConfigCard
      title={name}
      modifiedOn={modifiedOn}
      createdOn={createdOn}
      onClick={
        hasPermission(permissionEntity?.UPDATE)
          ? handleOpenEditModal
          : undefined
      }
      menuActions={actions}
    />
  );
};

type ConfigIssueTemplatesListProps = {
  view: "recordables" | "warrantyItems";
};
